import { Component, inject, OnInit, signal } from '@angular/core'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { firstValueFrom } from 'rxjs'

import { AppFeatures } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { BillingIconComponent } from '@ti/core/icons/billing'
import { LanguageService } from '@ti/core/ui-kit/i18n'
import { FormatDatePipe } from '@ti/core/ui-kit/i18n/pipes'
import { FleetHeaderComponent } from '@ti/core/ui-kit/layout/components/fleet-header'

interface BillingRecord {
  year: number
  month: number
  displayDate: Date
}

@Component({
  selector: 'app-tsp-admin-billing-page',
  templateUrl: 'billing-history-list.component.html',
  styleUrls: ['billing-history-list.component.scss'],
  imports: [FleetHeaderComponent, RouterLink, BillingIconComponent, FormatDatePipe, TranslatePipe],
})
export class BillingHistoryListComponent implements OnInit {
  protected readonly api = inject(ApiService)
  protected readonly router = inject(Router)
  protected readonly activatedRoute = inject(ActivatedRoute)
  protected readonly lang = inject(LanguageService)
  // Enums
  protected readonly AppFeatures = AppFeatures
  // State
  protected records = signal<BillingRecord[]>([])
  protected title = signal<string>('')
  protected tspId = signal<number>(0)

  async ngOnInit() {
    const params = await firstValueFrom(this.activatedRoute.paramMap)

    try {
      const tspId = parseInt(params.get('tspId') ?? '')

      if (!tspId) {
        return this.goBack()
      }

      const tsp = await this.api.ti.accounts.one(tspId)
      this.tspId.set(tspId)
      this.records.set(
        tsp.billingStartDate ? this.generateBillingRecords(new Date(tsp.billingStartDate)) : [],
      )
      const label = await this.lang.translate('tsp-admin.billing.history')
      this.title.set(`${label}: ${tsp.name}`)
    } catch {
      return this.goBack()
    }
  }

  generateBillingRecords(tspCreatedAt: Date, today: Date = new Date()): BillingRecord[] {
    const invoices: BillingRecord[] = []

    let currentYear = tspCreatedAt.getFullYear()
    let currentMonth = tspCreatedAt.getMonth()

    const endYear = today.getFullYear()
    const endMonth = today.getMonth()

    while (currentYear < endYear || (currentYear === endYear && currentMonth < endMonth)) {
      // For display and month identification, using the first day of the month at UTC midnight
      const displayDate = new Date(currentYear, currentMonth, 1, 0, 0, 0)

      invoices.push({
        year: currentYear,
        month: currentMonth + 1, // Month starts from 0
        displayDate,
      })

      currentMonth++
      if (currentMonth > 11) {
        currentMonth = 0
        currentYear++
      }
    }

    // Sort invoices in descending order if needed
    invoices.sort((a, b) => b.displayDate.getTime() - a.displayDate.getTime())
    return invoices
  }

  goBack() {
    return this.router.navigate(['billing'])
  }
}
